/**
 * Annotation Processing Utilities
 * Extracted from ResultAndRevision.js for better organization
 */

import { getColorMapForCategory } from './colorMaps.js';

/**
 * Apply inline annotations to paragraph text using character positions
 */
export function applyInlineAnnotations(text, annotations, colorMap) {
  // Handle undefined or null text
  if (!text) {
    return [];
  }
  
  if (!annotations || annotations.length === 0) {
    return [{ text, isHighlighted: false }];
  }

  // Filter out invalid annotations before processing
  const validAnnotations = annotations.filter(annotation => {
    if (!annotation || typeof annotation.start !== 'number' || typeof annotation.end !== 'number') {
      return false;
    }
    
    // Check if annotation positions are within text bounds
    if (annotation.start < 0 || annotation.end > text.length || annotation.start >= annotation.end) {
      return false;
    }
    
    return true;
  });

  // Sort valid annotations by start position
  const sortedAnnotations = validAnnotations.sort((a, b) => a.start - b.start);
  
  const segments = [];
  let currentPos = 0;
  
  for (const annotation of sortedAnnotations) {
    const { type, start, end, comment } = annotation;
    
    // Additional safety check (should not be needed after filtering, but just in case)
    if (start < currentPos) {
      continue;
    }
    
    // Add text before annotation
    if (start > currentPos) {
      segments.push({
        text: text.slice(currentPos, start),
        isHighlighted: false
      });
    }
    
    // Add highlighted annotation
    segments.push({
      text: text.slice(start, end),
      isHighlighted: true,
      type,
      comment,
      color: colorMap[type] || '#3498db'
    });
    
    currentPos = end;
  }
  
  // Add remaining text after last annotation
  if (currentPos < text.length) {
    segments.push({
      text: text.slice(currentPos),
      isHighlighted: false
    });
  }
  
  return segments;
}

/**
 * Strip inline annotations from text
 */
export function stripInlineAnnotations(text) {
  return text.replace(/\{\{[PCLMGSE]:.*?\}\}/g, '').replace(/\\n/g, '\n');
}

/**
 * Decode Unicode escape sequences in text
 */
function decodeUnicodeEscapes(text) {
  try {
    return text.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
      return String.fromCharCode(parseInt(code, 16));
    });
  } catch (e) {
    console.warn('Failed to decode Unicode escape sequences:', e);
    return text;
  }
}

/**
 * Format feedback content and style [TEACHER] tags
 */
export function formatFeedbackContent(content, category) {
  if (!content) return '';
  
  // Decode Unicode escape sequences first
  let processed = decodeUnicodeEscapes(content);
  
  // Clean up double TEACHER tags - replace multiple [TEACHER] tags with just one
  processed = processed.replace(/(\s*\[TEACHER\]\s*)+/g, ' [TEACHER]').trim();
  
  // Get the appropriate color map for this category
  const colorMap = getColorMapForCategory(category);
  
  // Get valid codes for this category using the same logic as processInlineFeedback
  let codeSet = '[PCLM]'; // Default
  if (category === 'GCSE_Shakespeare' || category === 'GCSE_19th' || 
      category === 'GCSE_Poetry' || category === 'GCSE_Inspector') {
    codeSet = '[ALCM]';
  } else if (category === 'GCSE_French_Q1' || category === 'GCSE_French_Q2' || 
             category === 'GCSE_Spanish_Q1' || category === 'GCSE_Spanish_Q2' || 
             category === 'GCSE_German_Q1' || category === 'GCSE_German_Q2' ||
             category === 'GCSE_FR_Grammar' || category === 'GCSE_ES_Grammar' || 
             category === 'GCSE_DE_Grammar') {
    codeSet = '[CLA]';
  } else if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
    codeSet = '[KCEL]'; // VCE Sections A & C
  } else if (category === 'VCE_English_SectionB') {
    codeSet = '[EPSL]'; // VCE Section B
  } else if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
             category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
             category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB') {
    codeSet = '[ESAL]'; // Australian HSC
  } else if (category === 'Aiste' || category === 'Grammar Checker' || category === 'IE_Oral') {
    codeSet = '[GCS]';
  } else if (category && category.startsWith('Filiocht_')) {
    codeSet = '[GC]';
  } else if (category && category.startsWith('FR_') || category && category.startsWith('ES_')) {
    codeSet = '[CL]';
  } else if (category && category.startsWith('DE_')) {
    codeSet = '[CE]';
  }
  
  // Check if the content contains the bracket pattern and extract just the content part
  const singleBracketRegex = new RegExp(`^\\{(${codeSet}|AO\\d+):\\s*(.+?)\\}\\s*(\\[TEACHER\\])?$`, 's');
  const match = processed.match(singleBracketRegex);
  
  if (match) {
    // Content has the bracket format, extract the actual content and type
    const type = match[1];
    const actualContent = match[2];
    const teacherTag = match[3] || '';
    
    // Format with colored type indicator
    processed = `<span style="color: ${colorMap[type]}; font-weight: bold;">${type}:</span> ${actualContent}`;
    
    // Add TEACHER tag if it was present
    if (teacherTag) {
      processed += ' <span style="background-color: #3498db; color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-left: 4px;">TEACHER</span>';
    }
  } else {
    // Content doesn't have bracket format, just process [TEACHER] tags
    processed = processed.replace(
      /\s*\[TEACHER\]\s*/g,
      ' <span style="background-color: #3498db; color: white; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-left: 4px;">TEACHER</span>'
    );
  }
  
  return processed;
}

/**
 * processInlineFeedback(text, category)
 * Converts inline annotations into colored spans and block feedback into styled divs.
 */
export function processInlineFeedback(text, category) {
  if (!text) return '';
  let processed = text.replace(/\n{3,}/g, '\n\n');

  // Get the appropriate color map for this category
  const colorMap = getColorMapForCategory(category);

  // Get valid codes for this category
  let codeSet = '[PCLM]'; // Default
  if (category === 'GCSE_Shakespeare' || category === 'GCSE_19th' || 
      category === 'GCSE_Poetry' || category === 'GCSE_Inspector') {
    codeSet = '[ALCM]';
  } else if (category === 'GCSE_French_Q1' || category === 'GCSE_French_Q2' || 
             category === 'GCSE_Spanish_Q1' || category === 'GCSE_Spanish_Q2' || 
             category === 'GCSE_German_Q1' || category === 'GCSE_German_Q2' ||
             category === 'GCSE_FR_Grammar' || category === 'GCSE_ES_Grammar' || 
             category === 'GCSE_DE_Grammar') {
    codeSet = '[CLA]';
  } else if (category === 'VCE_English_SectionA' || category === 'VCE_English_SectionC') {
    codeSet = '[KCEL]'; // VCE Sections A & C
  } else if (category === 'VCE_English_SectionB') {
    codeSet = '[EPSL]'; // VCE Section B
  } else if (category === 'Australian_HSC' || category === 'Australian_Textual' || category === 'Australian_Critical' ||
             category === 'English_HSC_Textual' || category === 'English_HSC_Critical' || category === 'English_HSC_Texts' || 
             category === 'English_HSC_Craft' || category === 'English_HSC_CraftA' || category === 'English_HSC_CraftB') {
    codeSet = '[ESAL]'; // Australian HSC
  } else if (category === 'Aiste' || category === 'Grammar Checker' || category === 'IE_Oral') {
    codeSet = '[GCS]';
  } else if (category.startsWith('Filiocht_')) {
    codeSet = '[GC]';
  } else if (category.startsWith('FR_') || category.startsWith('ES_')) {
    codeSet = '[CL]';
  } else if (category.startsWith('DE_')) {
    codeSet = '[CE]';
  }

  // Double-bracket pattern, e.g. {{G: text}} or {{P: text}}
  const doubleBracketRegex = new RegExp(`\\{\\{(${codeSet}):\\s*([^}]+?)\\}\\}`, 'g');
  processed = processed.replace(doubleBracketRegex, (match, type, content) => {
    return `<span style="background-color: ${colorMap[type]}33; border-bottom: 2px solid ${colorMap[type]};">${content}</span>`;
  });

  // Single-bracket pattern, e.g. {G: text} or {P: text}
  const singleBracketRegex = new RegExp(`\\{(${codeSet}):\\s*([^}]+?)\\}`, 'g');
  processed = processed.replace(singleBracketRegex, (match, type, content) => {
    return `<div style="margin: 5px 0; padding: 8px; background-color: ${colorMap[type]}22; border-left: 4px solid ${colorMap[type]};">${content}</div>`;
  });

  return processed;
} 